import { DialogBox } from "lib/@components/dialogBox"
import { useEffect, useMemo, useState } from "react"
import { PagedRepeat } from "lib/@components/paged-repeat"
import {
    Box,
    Button,
    Checkbox,
    IconButton,
    InputAdornment,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { findInChildren, parentTreeItem } from "library/tree"
import { If } from "lib/@components/switch"
import { Repeat } from "lib/@components/repeat"
import { Else } from "lib/@components/else"
import { Bound } from "lib/@components/binding/Bound"
import { ScheduleTreeItem } from "./schedule-tree-item"
import { StandardSwitch } from "lib/@components/binding/bound-components"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { SCHEDULE } from "library/constants"
import { useTreeItem } from "routes/schedule/lib/useTreeItem"
import { getTreeItem } from "routes/schedule/lib/getTreeItem"
import { getSchedulesLockStatus } from "authoring/routes/project/controller/get-schedules-lock-status"
import { uniqueId } from "library/database/split-id"
import { isScheduleSelectable } from "../lib/is-schedule-selectable"
import { compareByCode } from "authoring/lib/compare-by-code"

export function ScheduleSelectorDialog() {
    const { target: project } = useBoundContext()
    const { scopedScheduleIds } = project ?? {}
    const [parentId, setParentId] = useState("schedules")
    const [search, setSearch] = useState("")
    const [selected, setSelected] = useState({})
    const [initialised, setInitialised] = useState(false)
    const [showSelectedOnly, setShowSelectedOnly] = useState(false)
    const parent = useTreeItem(parentId)
    const [allSchedules] = useState(initialiseAllSchedules)

    const scheduleLookup = useMemo(() => (allSchedules ?? []).keyBy("_id"), [allSchedules])
    const scheduleIds = useMemo(() => (allSchedules ?? []).map(({ _id }) => uniqueId(_id)), [allSchedules])

    const lockStatusList = getSchedulesLockStatus.useResults({ projectId: project?.id, scheduleIds })
    const lockStatusItems = useMemo(() => lockStatusList ?? [], [lockStatusList])

    const lockDataById = useMemo(() => {
        const lockDataByKey = {}
        lockStatusItems.forEach(({ scheduleId, ...lockData }) => {
            lockDataByKey[`${scheduleId}:sfg20/schedule`] = lockData
        })
        return lockDataByKey
    }, [lockStatusItems])

    const loading = !parent || !allSchedules || !scheduleLookup

    const selectedIds = Object.keys(selected)
    const childSchedules = parent?.id === "schedules" ? [] : findInChildren(parent, (c) => c?.$?.type === SCHEDULE)
    const activeList = search ? searchAllSchedules(allSchedules, search) : childSchedules
    const selectableActiveList = activeList.filter((s) => isScheduleSelectable(s, lockDataById))
    const selectedFromActive = selectableActiveList.filter(({ _id }) => selected[_id])
    const allSelected = selectableActiveList.length > 0 && selectableActiveList.length === selectedFromActive.length
    const someSelected = selectedFromActive.length > 0 && !allSelected

    const list = useMemo(() => {
        if (loading) return []
        if (showSelectedOnly) return Object.values(selected).sort(compareByCode)
        if (search) return activeList.sort(compareByCode)
        return sortTreeChildren(parent?.children ?? parent?.$?.children ?? [])
    }, [loading, search, parent, selected, showSelectedOnly, activeList])

    useEffect(() => {
        if (loading || initialised || !scopedScheduleIds?.length) return
        const existing = (scopedScheduleIds ?? [])
            .map((id) => scheduleLookup[`${id}:sfg20/schedule`])
            .filter(Boolean)
            .keyBy("_id")

        setSelected(existing ?? {})
        setInitialised(true)
    }, [scheduleLookup, project, initialised, loading, scopedScheduleIds])

    return (
        <DialogBox
            title={"Add Schedules"}
            okCaption={selectedIds.length > 0 ? `Add (${selectedIds.length})` : "Add"}
            okDisabled={selectedIds.length === 0}
            value={selectedIds.map((id) => id.split(":")[0])}
            data-cy="schedule-selector-dialog"
        >
            <Stack spacing={2}>
                <TextField
                    data-cy="search-input"
                    placeholder="Search"
                    value={search}
                    onChange={({ target }) => setSearch(target.value)}
                    size="small"
                    variant="standard"
                    fullWidth
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="Clear search"
                                        edge="end"
                                        size="small"
                                        onClick={() => setSearch("")}
                                        disabled={!search}
                                    >
                                        <Iconify icon="material-symbols:close-rounded" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />
                <Stack direction="row" alignItems="center" spacing={2}>
                    <If truthy={parent?.id !== "schedules"}>
                        <Button
                            size="small"
                            onClick={handleBack}
                            data-cy="previous-folder-button"
                            startIcon={<Iconify icon="mdi:arrow-up" />}
                        >
                            <Typography variant="subtitle2" color="text.secondary" data-cy="previous-folder-title">
                                {parent?.[parentTreeItem].label}
                            </Typography>
                        </Button>
                    </If>
                    {parent?.label && (
                        <Typography variant="subtitle2" data-cy="folder-title">
                            {parent?.label}
                        </Typography>
                    )}
                    <Box flex={1} />
                    <StandardSwitch
                        data-cy="show-selected"
                        value={showSelectedOnly}
                        label={`Show Selected Only${selectedIds.length ? ` (${selectedIds.length})` : ""}`}
                        onChange={setShowSelectedOnly}
                    />
                </Stack>
                <If truthy={loading}>
                    <Stack>
                        <Repeat
                            list={Array.from({ length: 5 })}
                            item={<Skeleton sx={{ margin: 0, height: "42px" }} />}
                        />
                    </Stack>
                    <Else>
                        <Stack>
                            <If truthy={parent?.id !== "schedules" || !!search}>
                                <Box display={"flex"} alignItems={"center"} gap={2} px={2}>
                                    <Checkbox
                                        checked={allSelected}
                                        indeterminate={someSelected}
                                        onClick={() => selectAllChildren(activeList, allSelected)}
                                        disabled={!selectableActiveList.length}
                                        data-cy="select-all-checkbox"
                                    />
                                    <Typography>Select All</Typography>
                                </Box>
                            </If>
                            <Bound
                                selected={selected}
                                onSelect={handleSelect}
                                onOpen={handleOpen}
                                onSelectAll={selectAllChildren}
                                lockDataById={lockDataById}
                            >
                                <PagedRepeat list={list} item={<ScheduleTreeItem />} showTop={true} />
                            </Bound>
                        </Stack>
                    </Else>
                </If>
            </Stack>
        </DialogBox>
    )

    function selectAllChildren(children = childSchedules, deselect = allSelected) {
        const selectable = children.filter((schedule) => isScheduleSelectable(schedule, lockDataById))

        setSelected((current) => {
            if (deselect) {
                const targetIds = selectable.map("_id")
                const newSelection = { ...current }
                targetIds.forEach((id) => delete newSelection[id])
                return newSelection
            }

            const transformed = selectable.reduce((acc, schedule) => {
                acc[schedule._id] = schedule
                return acc
            }, {})
            return { ...current, ...transformed }
        })
    }

    function handleSelect(newSelection) {
        setSelected(newSelection)
    }

    function handleOpen({ id }) {
        setParentId(id)
    }

    function handleBack() {
        if (parent?.id === "schedules") return
        setParentId(parent?.[parentTreeItem]?.id)
    }
}

function sortTreeChildren(children) {
    const groups = []
    const schedules = []
    for (const child of children) {
        if (child?.$?.type === SCHEDULE) {
            schedules.push(child)
        } else {
            groups.push(child)
        }
    }
    return [...groups, ...schedules.sort(compareByCode)]
}

function searchAllSchedules(allSchedules = [], searchTerm) {
    searchTerm = searchTerm?.trim()?.toLowerCase()

    const filtered = allSchedules.filter(
        ({ code, label }) => label?.toLowerCase()?.includes(searchTerm) || code?.toLowerCase()?.includes(searchTerm)
    )

    return filtered
}

function initialiseAllSchedules() {
    const seen = new Set()
    return (
        findInChildren(getTreeItem("schedules"), (c) => {
            const isSchedule = c?.$?.type === SCHEDULE
            if (isSchedule && !seen.has(c._id)) {
                seen.add(c._id)
                return true
            }
            return false
        }) ?? []
    )
}
